import * as yup from 'yup';
import { useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import DOMPurify from 'dompurify';
import { useGenerateOutreach, useOutreachEmailTemplate } from '../../../../api/useOutreachCandidate';
import YupValidationErrors from '@/Utils/YupValidationErrors';

export interface OutreachCandidateEmailTemplateForm {
    subject: string;
    emailHtml: string;
    emailHtmlTextLength: number;
}

const emailCandidateFormSchema: yup.SchemaOf<OutreachCandidateEmailTemplateForm> = yup.object({
    emailHtml: yup.string().required(),
    emailHtmlTextLength: yup
        .number()
        .required()
        .moreThan(0, YupValidationErrors.Required)
        .max(5000, YupValidationErrors.PleaseLimitDescriptionError),
    subject: yup
        .string()
        .required(YupValidationErrors.Required)
        .max(500, YupValidationErrors.MaxSymbolsValidationError),
});

export const useEmailCandidateForm = () => {
    // this should be the only place to handle ai generated outreach
    // unless if you are using useSWR/useSWRImmut
    const { trigger } = useGenerateOutreach();
    const { data: savedTemplate } = useOutreachEmailTemplate();

    return useForm({
        resolver: yupResolver(emailCandidateFormSchema),
        mode: 'all',
        defaultValues: async () => {
            const defaultValues: OutreachCandidateEmailTemplateForm = {
                emailHtml: '',
                emailHtmlTextLength: 0,
                subject: '',
            };

            if (savedTemplate) {
                const parser = new DOMParser();
                const dom = parser.parseFromString(savedTemplate.templateHtml, 'text/html');

                defaultValues.emailHtml = DOMPurify.sanitize(savedTemplate.templateHtml);
                defaultValues.emailHtmlTextLength = dom.body?.textContent?.length || 0;
                defaultValues.subject = savedTemplate.subject;
            } else {
                try {
                    const generatedOutreach = await trigger();
                    const emailHtml = outreachEmailMessageBodyToHtml(generatedOutreach.emailMessageBody);

                    defaultValues.emailHtml = DOMPurify.sanitize(emailHtml);
                    defaultValues.emailHtmlTextLength = generatedOutreach.emailMessageBody.length;
                    defaultValues.subject = generatedOutreach.emailSubject;
                } catch {
                    return defaultValues;
                }
            }

            return defaultValues;
        },
    });
};

type EmailCandidateForm = yup.InferType<typeof emailCandidateFormSchema>;
export const useEmailCandidateFormContext = () => useFormContext<EmailCandidateForm>();

// only use for outreach email generated by AI
const outreachEmailMessageBodyToHtml = (emailMessageBody: string) => {
    const nodes = emailMessageBody.split(/\n/g);
    const encodedEmailMessageBody = nodes
        // fully encode to html paragraphs
        .map((node) => `<p>${node}</p>`)
        // transform your nodes here
        .map((node, i, arr) => {
            const listSearchValue = '<p>- ';

            // transform node starting with dash to unordered list
            if (node.startsWith(listSearchValue)) {
                const newNode = `<li>${node.replace(listSearchValue, '<p>')}</li>`;

                if (!arr[i - 1].startsWith(listSearchValue)) {
                    return `<ul>${newNode}`;
                } else if (!arr[i + 1].startsWith(listSearchValue)) {
                    return `${newNode}</ul>`;
                } else {
                    return newNode;
                }
            } else {
                return node;
            }
        })
        .join('');

    return encodedEmailMessageBody;
};
